import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { capitalize } from '@/utils/stringUtils';
import { Icon } from '@/components/UI/Icon/Icon';
import { Select } from '@/components/UI/Select/Select';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import { WeaveInput } from '@/components/UI/WeaveInput/WeaveInput';
import { RoomData } from './SpacePlanningTypes';
import { unitConversionService as unitService } from '@/services/unit-conversion.service';
import { materialService } from '@/services/material.service';
import { MaterialCategory } from '@/models/Materials';
import { MaterialDetails } from './MaterialDetails';
import styles from './styles.module.scss';
import { calculateMaterialScoreFromIds, MaterialScoreDetails } from '@/services/score/material';
import { WeaveIconButton } from '../UI/WeaveIconButton/WeaveIconButton';
import { MaterialBreakdown } from './MaterialBreakdown';
import { calculateBioScoreBonus, calculateFinalBioScoreForRoom } from '@/services/score/bioScore';
import { WeaveDropdown, WeaveDropdownDivider, WeaveDropdownOption } from '../UI/WeaveDropdown';

interface RoomProps {
	data: RoomData;
	path: string;
	allowEditing?: boolean;
	showLevelInTitle?: boolean;
}

export const Room = ({
	data,
	path,
	allowEditing = true,
	showLevelInTitle = false,
}: RoomProps) => {
	const { updateRoom, removeRoom } = useSpacePlanningStore();
	const [materialsBioScore, setMaterialsBioScore] = useState<MaterialScoreDetails | null>(null);
	const [roomBioScore, setRoomBioScore] = useState<number>(0);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
	const [editedTitle, setEditedTitle] = useState<string>(data.spaceDescription);
	const titleInputRef = useRef<HTMLElement | null>(null);
	const areaTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Calculate Material BioScore based on input data
	const calculateMaterialsBioScore = (roomData: RoomData): number => {
		if (
			!roomData.materials?.wall ||
			!roomData.materials?.floor ||
			!roomData.materials?.ceiling
		) {
			setMaterialsBioScore(null);
			return 0;
		}

		const result = calculateMaterialScoreFromIds(
			{
				wallMaterialId: roomData.materials.wall,
				floorMaterialId: roomData.materials.floor,
				ceilingMaterialId: roomData.materials.ceiling,
				wallArea: roomData.wallAreaTotal ?? 0,
				floorArea: roomData.ASF,
				ceilingArea: roomData.ASF,
			},
			(id) => materialService.getMaterialById(id)
		);

		if (!result) {
			setMaterialsBioScore(null);
			return 0;
		}

		setMaterialsBioScore(result);
		return result.score;
	};

	useEffect(() => {
		calculateMaterialsBioScore(data);

		// Calculate final BioScore for room on load
		const { displayedScore } = calculateFinalBioScoreForRoom(data);
		setRoomBioScore(displayedScore);
	}, []);

	useLayoutEffect(() => {
		if (isEditingTitle) {
			titleInputRef.current?.focus();
		}
	}, [isEditingTitle]);

	const handleRoomDelete = () => {
		if (!allowEditing) return;
		removeRoom(path, data);
	};

	const handleTitleEdit = async () => {
		if (!allowEditing) return;
		if (editedTitle.trim() === '' || editedTitle === data.spaceDescription) {
			setIsEditingTitle(false);
			setEditedTitle(data.spaceDescription);
			return;
		}

		const updatedData = { ...data, spaceDescription: editedTitle.trim() };
		await updateRoom(path, updatedData);
		setIsEditingTitle(false);
	};

	const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleTitleEdit();
		} else if (e.key === 'Escape') {
			setIsEditingTitle(false);
			setEditedTitle(data.spaceDescription);
		}
	};

	const handleRoomValuesChange = async (
		key: keyof RoomData,
		value: number | RoomData[keyof RoomData]
	) => {
		if (!allowEditing) return;
		const updatedData = { ...data, [key]: value };

		if (key === 'materials' || key === 'ASF' || key === 'wallAreaTotal') {
			const matScore = calculateMaterialsBioScore(updatedData);
			updatedData.scoreBreakdown = {
				...updatedData.scoreBreakdown,
				materials: matScore,
				sunlight: updatedData.scoreBreakdown?.sunlight ?? undefined,
				view: updatedData.scoreBreakdown?.view ?? undefined,
			};
		}

		if (key === 'waterFeatures' || key === 'acoustics') {
			const envScore = calculateBioScoreBonus({
				waterAudibility: Number(updatedData.waterFeatures?.audibility ?? 0) > 0,
				waterVisibility: Number(updatedData.waterFeatures?.visibility ?? 0) > 0,
				acousticTreatment: Number(updatedData.acoustics?.planned ?? 0) > 0,
			});

			updatedData.scoreBreakdown = {
				...updatedData.scoreBreakdown,
				bonus: envScore,
			};
		}

		// Calculate final BioScore for room
		const { displayedScore } = calculateFinalBioScoreForRoom(updatedData);
		setRoomBioScore(displayedScore);

		updatedData.score = displayedScore;

		updateRoom(path, updatedData);
	};

	const handleAreaChange = useCallback(
		(rawValue: string) => {
			if (!allowEditing) return;
			if (!rawValue) return;

			if (areaTimeoutRef.current) {
				clearTimeout(areaTimeoutRef.current);
			}

			areaTimeoutRef.current = setTimeout(() => {
				const numericValue = parseFloat(rawValue);

				const newArea =
					unitService.getPreferredUnit('area') === 'ft2'
						? unitService.convert(numericValue, 'ft2', 'm2', 2)
						: numericValue;

				handleRoomValuesChange('ASF', newArea);
			}, 500);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[unitService, handleRoomValuesChange]
	);

	useEffect(() => {
		return () => {
			if (areaTimeoutRef.current) {
				clearTimeout(areaTimeoutRef.current);
			}
		};
	}, []);

	const renderGroupedMaterialOptions = (category: MaterialCategory) => {
		const groups = materialService.getGroupedMaterialsByCategory(category);
		const unassignedOption = (
			<WeaveDropdownOption key={`${category}-unassigned`} value="">
				Undefined
			</WeaveDropdownOption>
		);
		const unassignedDivider = <WeaveDropdownDivider key={`${category}-unassigned-divider`} />;

		const groupedOptions = groups.flatMap((group, groupIndex) => {
			const groupHeader = (
				<WeaveDropdownOption
					key={`${category}-${group.label}-header`}
					value={`__${category}_${groupIndex}`}
					disabled
					inert
					style={{ fontStyle: 'italic' }}
				>
					{group.label}
				</WeaveDropdownOption>
			);

			const groupOptions = group.options.map((option) => (
				<WeaveDropdownOption key={`${category}-${option.value}`} value={option.value}>
					{option.label}
				</WeaveDropdownOption>
			));

			const groupDivider =
				groupIndex < groups.length - 1 ? (
					<WeaveDropdownDivider key={`${category}-${group.label}-divider`} />
				) : null;

			return [
				<Fragment key={`${category}-${group.label}-fragment`}>
					{groupHeader}
					{groupOptions}
					{groupDivider}
				</Fragment>,
			];
		});

		return [unassignedOption, unassignedDivider, ...groupedOptions];
	};

	const YES_NO_OPTIONS = [
		{ label: 'Yes', value: 1 },
		{ label: 'No', value: 0 },
	];

	const WATER_FEATURE_FIELDS = ['visibility', 'audibility'] as const;
	const displayRoomTitle = showLevelInTitle
		? `Level ${data.level ?? '?'} - ${data.spaceDescription}`
		: data.spaceDescription;

	return (
		<>
			{/* Room Card */}
			<div className={styles.roomCard}>
				{/* Room Header */}
				<div className={styles.roomHeader} onClick={() => setIsExpanded((prev) => !prev)}>
					<div className={styles.roomTitleGroup}>
						<Icon
							className={`${styles.roomChevron} ${isExpanded ? styles.roomChevronOpen : ''}`}
							name="forma-icon-arrow-right"
						/>
						<div>
							{isEditingTitle ? (
								<div className={styles.titleEditContainer}>
									<WeaveInput
										className={styles.titleInput}
										type="text"
										value={editedTitle}
										onChange={(event: unknown) => {
											const target = event as {
												nativeEvent: { detail: { value: string } };
											};
											setEditedTitle(target.nativeEvent.detail.value);
										}}
										onKeyDown={handleTitleKeyDown}
										onBlur={handleTitleEdit}
										ref={titleInputRef}
									/>
									<span className={styles.subText}>(#{data.spaceId})</span>
								</div>
							) : (
								<h3
									className={styles.roomTitle}
									onClick={(event) => {
										if (!allowEditing) return;
										event.stopPropagation();
										setIsExpanded(true);
										setIsEditingTitle(true);
									}}
									style={{ cursor: allowEditing ? 'pointer' : 'default' }}
									title="Click to edit room name"
								>
									{displayRoomTitle}{' '}
									<span className={styles.subText}>(#{data.spaceId})</span>
								</h3>
							)}
							<div className={styles.bioScore}>BioScore: {roomBioScore}</div>
						</div>
					</div>

					<div
						className={styles.roomControls}
						onClick={(event) => event.stopPropagation()}
					>
						<div className={styles.inputGroupSmall}>
							<label>Area ({unitService.getPreferredUnit('area')})</label>
							<WeaveInput
								className={styles.areaInput}
								type="number"
								disabled={!allowEditing}
								value={
									unitService.getPreferredUnit('area') === 'ft2'
										? unitService.toPreferredUnit(data.ASF, 'area', 'm2')
										: data.ASF
								}
								onChange={(event: unknown) => {
									const target = event as {
										nativeEvent: { detail: { value: string } };
									};
									const rawValue = target.nativeEvent.detail.value;
									handleAreaChange(rawValue);
								}}
							/>
						</div>
						<div className={styles.inputGroupSmall}>
							<label>Count</label>
							<WeaveInput
								className={styles.qtyInput}
								type="number"
								disabled={!allowEditing}
								value={data.quantity.toString()}
								onChange={(event: unknown) => {
									const target = event as {
										nativeEvent: { detail: { value: string } };
									};
									if (!target.nativeEvent.detail.value) return;
									handleRoomValuesChange(
										'quantity',
										parseFloat(target.nativeEvent.detail.value)
									);
								}}
							/>
						</div>

						<WeaveIconButton
							className={styles.deleteBtn}
							onClick={handleRoomDelete}
							disabled={!allowEditing}
						>
							<Icon name="forma-trash-16" />
						</WeaveIconButton>
					</div>
				</div>

				{/* Room Body Grid */}
				{isExpanded && (
					<div className={styles.roomContent}>
						{/* Column 1: Materials */}
						<div className={styles.column}>
							<div className={styles.columnHeader}>
								<h4 className={styles.columnTitle}>Materials</h4>
								{materialsBioScore && (
									<div
										className={styles.columnIcon}
										id={`${data.spaceId}-${data.spaceDescription.replace(' ', '')}`}
									>
										<Icon name="forma-info-16" />
									</div>
								)}
							</div>

							{materialsBioScore && (
								<MaterialBreakdown
									id={`${data.spaceId}-${data.spaceDescription.replace(' ', '')}`}
									scoreData={materialsBioScore}
								/>
							)}
							<div className={styles.formGroup}>
								<label className={styles.formGroupLabel}>Wall</label>
								<div className={styles.selectWrapper}>
									<WeaveDropdown
										placeholder="Undefined"
										disabled={!allowEditing}
										value={data.materials?.wall ?? ''}
										onChange={({ value }) => {
											handleRoomValuesChange('materials', {
												...data.materials,
												wall: value,
											});
										}}
										style={{ width: '100%' }}
									>
										{renderGroupedMaterialOptions('wall')}
									</WeaveDropdown>
								</div>
								<MaterialDetails materialId={data.materials?.wall} />
							</div>
							<div className={styles.formGroup}>
								<label className={styles.formGroupLabel}>Floor</label>
								<div className={styles.selectWrapper}>
									<WeaveDropdown
										placeholder="Undefined"
										disabled={!allowEditing}
										value={data.materials?.floor ?? ''}
										onChange={({ value }) => {
											handleRoomValuesChange('materials', {
												...data.materials,
												floor: value,
											});
										}}
										style={{ width: '100%' }}
									>
										{renderGroupedMaterialOptions('floor')}
									</WeaveDropdown>
								</div>
								<MaterialDetails materialId={data.materials?.floor} />
							</div>
							<div className={styles.formGroup}>
								<label className={styles.formGroupLabel}>Ceiling</label>
								<div className={styles.selectWrapper}>
									<WeaveDropdown
										placeholder="Undefined"
										disabled={!allowEditing}
										value={data.materials?.ceiling ?? ''}
										onChange={({ value }) => {
											handleRoomValuesChange('materials', {
												...data.materials,
												ceiling: value,
											});
										}}
										style={{ width: '100%' }}
									>
										{renderGroupedMaterialOptions('ceiling')}
									</WeaveDropdown>
								</div>
								<MaterialDetails materialId={data.materials?.ceiling} />
							</div>
						</div>

						{/* Column 2: Environment */}
						<div className={styles.column}>
							<div className={styles.columnHeader}>
								<h4 className={styles.columnTitle}>Environment</h4>
							</div>
							<div className={styles.grid2Col}>
								{WATER_FEATURE_FIELDS.map((field) => (
									<div key={field} className={styles.formGroup}>
										<label className={styles.formGroupLabel}>
											Water {capitalize(field)}
										</label>
										<div className={styles.selectWrapper}>
											<Select
												disabled={!allowEditing}
												options={YES_NO_OPTIONS}
												value={data.waterFeatures?.[field] ?? 0}
												onChange={(event: unknown) => {
													const target = event as {
														detail: { value: number };
													};
													handleRoomValuesChange('waterFeatures', {
														...data.waterFeatures,
														[field]: Number(target.detail.value),
													});
												}}
											/>
										</div>
									</div>
								))}

								<div className={styles.fullWidth}>
									<div className={styles.formGroup}>
										<label className={styles.formGroupLabel}>
											Acoustic treatment
										</label>
										<div className={styles.selectWrapper}>
											<div style={{ width: '100%' }}>
												<Select
													disabled={!allowEditing}
													options={YES_NO_OPTIONS}
													value={data.acoustics?.planned ?? 0}
													onChange={(event: unknown) => {
														const target = event as {
															detail: { value: number };
														};
														handleRoomValuesChange('acoustics', {
															...data.acoustics,
															planned: Number(target.detail.value),
														});
													}}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};
